// ============================================
// 大运流年展示组件 - 默认全部展开
// ============================================

import { useState, useEffect, useRef } from "react";
import {
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { getGrey, getCellColor } from "../StyleControl";
import { getLiuNianByDaYun, getShiShen, fetchJieQiDates } from "../../utils/baziCalculator";
import LiuYueRiShi from "./LiuYueRiShi";

// 干支十神显示组件（天干地支左侧，十神右侧）
function GanZhiWithShen({ gan, zhi, ganShen, zhiShen }) {
  return (
    <Stack direction="column" spacing={0.1}>
      {/* 天干行 */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: "bold",
            color: getCellColor(null, gan),
            minWidth: 20,
          }}
        >
          {gan}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#666" }}>
          {ganShen}
        </Typography>
      </Stack>
      {/* 地支行 */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: "bold",
            color: getCellColor(null, zhi),
            minWidth: 20,
          }}
        >
          {zhi}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#666" }}>
          {zhiShen[0]}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function DaYunLiuNian({
  birthYear,
  daYunStartYear,
  qiYunStr,
  currentDaYun,
  currentLiuNian,
  currentLiuYue,
  currentLiuRi,
  currentLiuShi,
  onChange,
  daYun,
  riGan,
}) {
  const [selectedDaYun, setSelectedDaYun] = useState(currentDaYun);
  const [selectedLiuNian, setSelectedLiuNian] = useState(currentLiuNian);
  const [liuNianData, setLiuNianData] = useState({});
  const [jieQiCache, setJieQiCache] = useState({});
  const daYunScrollRef = useRef(null);
  const selectedButtonRef = useRef(null);

  // 使用 ref 避免依赖循环
  const jieQiCacheRef = useRef(jieQiCache);
  useEffect(() => {
    jieQiCacheRef.current = jieQiCache;
  }, [jieQiCache]);

  // 当切换大运时，获取该大运10年的节气数据（优先从缓存获取）
  useEffect(() => {
    if (selectedDaYun && riGan) {
      const years = [];
      for (let i = 0; i < 10; i++) {
        years.push(selectedDaYun + i);
      }
      
      // 检查哪些年份需要获取（未缓存的）
      const yearsToFetch = years.filter(year => !jieQiCacheRef.current[year]);
      
      // 如果有未缓存的年份，获取它们
      if (yearsToFetch.length > 0) {
        Promise.all(
          yearsToFetch.map(year => 
            fetchJieQiDates(year).then(data => ({ year, data }))
          )
        ).then(results => {
          // 更新缓存
          setJieQiCache(prev => {
            const newCache = { ...prev };
            results.forEach(({ year, data }) => {
              newCache[year] = data;
            });
            return newCache;
          });
          
          // 使用所有数据（缓存+新获取）更新流年数据
          const allCache = { ...jieQiCacheRef.current };
          results.forEach(({ year, data }) => {
            allCache[year] = data;
          });
          const data = getLiuNianByDaYun(riGan, selectedDaYun, allCache);
          setLiuNianData(data);
        });
      } else {
        // 所有年份都已缓存，直接使用缓存数据
        const data = getLiuNianByDaYun(riGan, selectedDaYun, jieQiCacheRef.current);
        setLiuNianData(data);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDaYun, riGan]);

  useEffect(() => {
    if (currentDaYun) {
      setSelectedDaYun(currentDaYun);
    }
  }, [currentDaYun]);

  useEffect(() => {
    if (currentLiuNian) {
      setSelectedLiuNian(currentLiuNian);
    }
  }, [currentLiuNian]);

  // 自动滚动到选中大运的位置
  useEffect(() => {
    if (selectedDaYun && selectedButtonRef.current && daYunScrollRef.current) {
      const container = daYunScrollRef.current;
      const selectedButton = selectedButtonRef.current;
      
      // 计算选中按钮在容器中的位置
      const containerRect = container.getBoundingClientRect();
      const buttonRect = selectedButton.getBoundingClientRect();
      
      // 滚动到中间位置
      const scrollLeft = buttonRect.left - containerRect.left + container.scrollLeft - containerRect.width / 2 + buttonRect.width / 2;
      
      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth"
      });
    }
  }, [selectedDaYun]);

  const handleDaYunSelect = (value) => {
    if (value !== null) {
      setSelectedDaYun(value);
      const defaultLiuNian = value;
      setSelectedLiuNian(defaultLiuNian);
      onChange(value, defaultLiuNian, currentLiuYue, currentLiuRi, currentLiuShi);
    }
  };

  const handleLiuNianDetailChange = (liuNian, liuYue, liuRi, liuShi) => {
    setSelectedLiuNian(liuNian);
    onChange(selectedDaYun, liuNian, liuYue, liuRi, liuShi);
  };

  const daYunList = Object.entries(daYun || {}).map(([year, zhu]) => ({
    year: Number(year),
    zhu,
  }));

  const scrollableSx = {
    display: "flex",
    flexWrap: "nowrap",
    overflowX: "auto",
    gap: 0.5,
    pb: 1,
    "&::-webkit-scrollbar": {
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#c1c1c1",
      borderRadius: "3px",
    },
  };

  return (
    <Stack direction="column" spacing={2} alignItems="stretch">
      <Typography color={getGrey()} variant="body2">
        起运：{qiYunStr}
      </Typography>

      <Stack direction="column" spacing={1}>
        <Typography variant="caption" color={getGrey()}>
          大运
        </Typography>
        <ToggleButtonGroup
          ref={daYunScrollRef}
          value={selectedDaYun}
          exclusive
          onChange={(e, value) => handleDaYunSelect(value)}
          sx={scrollableSx}
        >
          {daYunList.map(({ year, zhu }) => {
            let age = daYunStartYear - birthYear + 1 + "岁前";
            if (year > birthYear) {
              age = year - birthYear + 1 + "岁";
            }
            const ganShen = getShiShen(riGan, zhu.gan);
            const zhiShen = zhu.zhi_canggan.map((cg) => getShiShen(riGan, cg));
            const isSelected = year === selectedDaYun;
            return (
              <ToggleButton
                key={year}
                value={year}
                ref={isSelected ? selectedButtonRef : null}
                sx={{
                  p: 0.8,
                  minWidth: 60,
                  border: "1px solid #e0e0e0",
                  flexShrink: 0,
                }}
              >
                <Stack direction="column" alignItems="center" spacing={0.3}>
                  <Typography
                    sx={{ fontSize: 10, fontWeight: "bold", color: "#333" }}
                  >
                    {year}年
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: "#999" }}>
                    {age}
                  </Typography>
                  <GanZhiWithShen
                    gan={zhu.gan}
                    zhi={zhu.zhi}
                    ganShen={ganShen}
                    zhiShen={zhiShen}
                  />
                </Stack>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Stack>

      {selectedDaYun && riGan && (
        <LiuYueRiShi
          yuanGan={riGan}
          liuNianData={liuNianData}
          currentLiuNian={selectedLiuNian}
          currentLiuYue={currentLiuYue}
          currentLiuRi={currentLiuRi}
          currentLiuShi={currentLiuShi}
          onChange={handleLiuNianDetailChange}
          jieQiCache={jieQiCache}
        />
      )}
    </Stack>
  );
}
